import type { ZodError } from 'zod';

import type { Log, LogType } from '../../entities';
import type {
  PerformSafeLogRepository,
  RequestContextRepository,
} from '../../repositories';

const hierarchy = {
  debug: ['debug', 'info', 'warn', 'error'],
  info: ['info', 'warn', 'error'],
  warn: ['warn', 'error'],
  error: ['error'],
};

function parseError(rootCause?: unknown): Partial<{
  rootCause: unknown;
  stackTrace: string;
  metadata: Record<string, unknown>;
}> {
  if (!rootCause) {
    return {};
  }

  if (rootCause instanceof Error) {
    if (rootCause.name === 'ZodError') {
      return {
        rootCause: rootCause.name,
        stackTrace: rootCause.stack,
        metadata: (rootCause as ZodError).format(),
      };
    }

    if ('cause' in rootCause) {
      if (rootCause.cause instanceof Error) {
        return {
          rootCause: rootCause.message,
          stackTrace: rootCause.stack,
          metadata: {
            rootCause: {
              message: rootCause.cause.message,
              stackTrace: rootCause.cause.stack,
            },
          },
        };
      }
    }

    return {
      rootCause: rootCause.message,
      stackTrace: rootCause.stack,
    };
  }

  return {
    rootCause: JSON.stringify(rootCause),
  };
}

export type LogSimpleUCFactoryParams = {
  logger: PerformSafeLogRepository;
  getContext?: RequestContextRepository;
};

export async function logSimpleUC(
  { logger, getContext }: LogSimpleUCFactoryParams,
  level: LogType,
  log: Log,
): Promise<void> {
  if (hierarchy[level].includes(log.type)) {
    const { data, ...content } = log;
    const { rootCause, ...otherData } = data || {};
    const context = await getContext?.();

    const parsedData = {
      http: context,
      timestamp: new Date().toISOString(),
      ...otherData,
      ...parseError(rootCause),
    };

    void logger({
      ...content,
      data: parsedData,
    });
  }
}
